import {
  CopyShipperUrl,
  DisabledTracking,
  PingLocation,
  ResendInstallationReq,
} from '@assets';
import type { MenuGroupType } from '@components/Select/SelectInputSearchable';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled as isMobileFeatureFlagEnabled } from '@mobileStore/featureFlags/store';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';

export const ADD_PLAYLIST_ID = 'addPlaylist';
export const RATE_CARRIER_ID = 'rateCarrier';
export const LOAD_TRACKING_ID = 'loadTracking';
export const ASSIGN_LOAD_ID = 'assignLoad';
export const BOUNCE_CARRIER_MODAL_ID = 'bounceCarrierModal';
export const CARRIER_INSTRUCTION_MODAL_ID = 'carrierInstructionModal';
export const EDI_LOGS_ID = 'ediLogs';
export const EDI_LOAD_TENDER_ID = 'ediLoadTenderModal';
export const PAY_WITH_CREDIT_CARD_ID = 'payWithCreditCardModal';
export const UNLINKED_EDI_TENDER_ID = 'unlinkedEDITender';
export const ACCEPT_TRACKING_CHARGES_ID = 'acceptTrackingCharges';
export const CARRIER_DETAILS_VENDOR_HOLD_MODAL_ID =
  'carrierDetailsVendorHoldModal';
export const DOCUMEN_PAGE_MODAL_ADD_EDIT_ID = 'documentPageModalAddEdit';
export const NEARBY_CARRIER_MODAL_ID = 'nearbyCarrierModal';
// ts-unused-exports:disable-next-line
export const IMPORT_PROSPECT_MODAL_ID = 'importProspectModal';

export const ADD_BILLING_HOLD_NOTE = 'addBillingHoldNote';
export const EDIT_BILLING_HOLD_AND_NOTE = 'editBillingNote';
export const CARRIER_DETAILS_CARRIER_REVIEWS_MODAL_ID =
  'carrierDetailsCarrierReviewsModal';
// ts-unused-exports:disable-next-line
export const CARRIER_DETAILS_TRANSFLOW_MODAL_ID =
  'carrierDetailsTransflowModal';
export const IS_LTL_LOAD_MODAL_ID = 'IS_LTL_LOAD';

export const DELETE_COMMENT_DIALOG_ID = 'deleteCommentDialog';
export const DELETE_FACTORING_COMPANY_DIALOG_ID =
  'deleteFactoringCompanyDialog';

export const REMOVE_PROSPECT_DIALOG_ID = 'removeProspectDialog';
export const DELETE_ORDER_TEMPLATE_DIALOG_ID = 'deleteOrderTemplateDialog';
export const DELETE_BILLING_NOTE_COMMENT_DIALOG_ID =
  'deleteBillingNoteCommentDialog';
export const REMOVE_LOADBOARD_TRACKING_COMMENT_DIALOG_ID =
  'removeTrackingCommentDialog';
export const DELETE_TRACKING_COMMENT_DIALOG_ID = 'deleteTrackingCommentDialog';
export const DELETE_CUSTOMER_PAY_LINE_ITEM_DIALOG_ID =
  'deleteCustomerPayLineItemDialog';
export const DELETE_ADJUSTMENTS_LINE_ITEM_DIALOG_ID =
  'deleteAdjustmentsLineItemDialog';
export const DELETE_CUSTOMER_PAYMENTS_LINE_ITEM_DIALOG_ID =
  'deleteCustomerPaymentsLineItemDialog';
export const DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID =
  'deleteVendorPaymentsLineItemDialog';
export const DELETE_CARRIER_PAYMENTS_LINE_ITEM_DIALOG_ID =
  'deleteCarrierPaymentsLineItemDialog';
export const DELETE_CARRIER_PAY_ROW_DIALOG_ID = 'deleteCarrierPayRowDialog';
export const CUSTOMER_SETTING_AND_ALERT_ENABLE_SUGGESTED_CLASS_DIALOG_ID =
  'enableSuggestedClass';
export const CANCEL_LTL_CHANGES_DIALOG_ID = 'cancelLTLChanges';
export const DELETE_ADDRESS_BOOK_GRID_ITEM_DIALOG_ID =
  'deleteAddressBookGridItem';
export const DELETE_CARRIER_DETAILS_DIALOG_ID = 'deleteCarrierDetails';
export const DELETE_CARRIER_CARRIER_MATCHING_DIALOG_ID =
  'deleteCarrierMatching';
export const DELETE_SAVED_QUOTES_DIALOG_ID = 'deleteSavedQuotes';
export const DELETE_CUSTOMER_INVOICE_DIALOG_ID = 'deleteCustomerInvoice';
export const UNSETTLE_SETTLEMENT_DIALOG_ID = 'unsettleSettlementDialog';
export const DELETE_REVIEW_DOC_DIALOG_ID = 'deleteReviewDocumentDialog';
export const DELETE_UNKNOWN_DOC_DIALOG_ID = 'deleteUnknownDocumentDialog';
export const NOTICE_ASSIGNMENT_DOC_DIALOG_ID = 'noticeAssignmentDocumentDialog';
export const ACKNOWLEDGE_CARRIER_RESTRICTION_MODAL_ID =
  'acknowledgeCarrierRestrictionModal';
export const OVERRIDE_RESTRICTED_CARRIER_MODAL_ID =
  'overrideRestrictedCarrierModal';

export const loadStatusItems = [
  { label: 'Can Get', value: 'Can Get' },
  { label: 'Unassigned', value: 'Unassigned' },
  { label: 'Assigned', value: 'Assigned' },
  { label: 'Dispatched', value: 'Dispatched' },
  { label: 'At Shipper', value: 'At Shipper' },
  { label: 'Loaded', value: 'Loaded' },
  { label: 'In Transit', value: 'In Transit' },
  { label: 'Out On Delivery', value: 'Out On Delivery' },
  { label: 'At Receiver', value: 'At Receiver' },
  { label: 'Empty', value: 'Empty' },
  { label: 'Paid', value: 'Paid', hidden: true },
  { label: 'Void', value: 'Void', hidden: true },
  { label: 'Billed', value: 'Billed', hidden: true },
  { label: 'Closed', value: 'Closed', hidden: true },
];

const AUTO_MANAGED_LOAD_STATUSES = new Set([
  'At Shipper',
  'Loaded',
  'In Transit',
  'Out On Delivery',
  'At Receiver',
  'Empty',
]);

const AUTO_MANAGED_TOOLTIP =
  'This status updates automatically via entry of driver in/out times.';

const AUTO_MANAGED_MODES = ['TL', 'Partial'] as const;

const CARRIER_EXEMPT_STATUSES = new Set([
  'Can Get',
  'Unassigned',
  'Paid',
  'Void',
  'Billed',
  'Closed',
]);

const isCarrierAssignmentRequired = (): boolean =>
  isFeatureFlagEnabled(
    FeatureFlags.RequireCarrierAssignmentToProgressStatus,
  )() ||
  isMobileFeatureFlagEnabled(
    FeatureFlags.RequireCarrierAssignmentToProgressStatus,
  )();

const NO_CARRIER_ASSIGNED_TOOLTIP =
  'A carrier must be assigned before this status can be used.';

const requiresCarrierAssignment = (
  status: string | null | undefined,
): boolean => Boolean(status) && !CARRIER_EXEMPT_STATUSES.has(status as string);

export const carrierRequiredStatusError = (
  status: string | null | undefined,
  hasCarrier: boolean,
): string | null =>
  isCarrierAssignmentRequired() &&
  !hasCarrier &&
  requiresCarrierAssignment(status)
    ? `A carrier must be assigned before moving this load to ${status}.`
    : null;

export const getLoadStatusItemsForMode = (
  mode: string | null | undefined,
  useStatusStateMachine?: boolean | null,
  carrierGate?: { hasCarrier: boolean },
  autoStatusFlagEnabled?: boolean,
): {
  label: string;
  value: string;
  disabled?: boolean;
  hidden?: boolean;
  title?: string;
}[] => {
  const flagOn =
    autoStatusFlagEnabled ??
    isFeatureFlagEnabled(FeatureFlags.permission_auto_update_load_status)();
  const inWaterfall =
    flagOn &&
    AUTO_MANAGED_MODES.includes(mode as (typeof AUTO_MANAGED_MODES)[number]) &&
    useStatusStateMachine === true;
  const carrierMissing =
    isCarrierAssignmentRequired() &&
    carrierGate !== undefined &&
    !carrierGate.hasCarrier;

  if (!inWaterfall && !carrierMissing) return loadStatusItems;

  return loadStatusItems.map((item) => {
    if (carrierMissing && requiresCarrierAssignment(item.value)) {
      return { ...item, disabled: true, title: NO_CARRIER_ASSIGNED_TOOLTIP };
    }
    if (inWaterfall && AUTO_MANAGED_LOAD_STATUSES.has(item.value)) {
      return { ...item, disabled: true, title: AUTO_MANAGED_TOOLTIP };
    }
    return item;
  });
};

export const trackingOptions = [
  {
    label: 'Resend Installation Request',
    value: 'Resend',
    icon: ResendInstallationReq,
  },
  { label: 'Ping Location', value: 'PingLocation', icon: PingLocation },
  { label: 'Disable Tracking', value: 'Disable', icon: DisabledTracking },
];

export const truckerToolsTrackingOptions = [
  {
    label: 'Resend Installation Request',
    value: 'Resend',
    icon: ResendInstallationReq,
  },
  { label: 'Ping Location', value: 'PingLocation', icon: PingLocation },
  { label: 'Disable Tracking', value: 'Disable', icon: DisabledTracking },
  { label: 'Copy Shipper URL', value: 'CopyShipperUrl', icon: CopyShipperUrl },
];

export const fuelCharges = [
  'Spot Charge',
  'Line Haul',
  'Rate per Mile',
  'Rate per Pound',
  'Rate per Unit',
];
//NOTE: DUPLICATED FORMAT CHANGED TO TEST FUNCTIONALITY OF SELECT INPUT
export const lineItemsTypesForCarrierPayNew: MenuGroupType[] = [
  {
    label: 'Pricing',
    items: [
      { label: 'Line Haul', value: 'Line Haul' },
      { label: 'Rate per Mile', value: 'Rate per Mile' },
      { label: 'Rate per Pound', value: 'Rate per Pound' },
      { label: 'Rate per Unit', value: 'Rate per Unit' },
    ],
  },
  {
    label: 'Accessorials',
    items: [
      { label: 'Accessorial', value: 'Accessorial' },
      { label: 'Bucket Truck', value: 'Bucket Truck' },
      { label: 'Damage', value: 'Damage' },
      { label: 'Dead Head', value: 'Dead Head' },
      { label: 'Detention', value: 'Detention' },
      { label: 'Detention Loading', value: 'Detention Loading' },
      { label: 'Detention Unloading', value: 'Detention Unloading' },
      { label: 'Driver Assist', value: 'Driver Assist' },
      { label: 'Escort', value: 'Escort' },
      { label: 'Fuel', value: 'Fuel' },
      { label: 'Fumigation', value: 'Fumigation' },
      { label: 'Insurance', value: 'Insurance' },
      { label: 'Layover', value: 'Layover' },
      { label: 'Lumper', value: 'Lumper' },
      { label: 'Lumper Loading', value: 'Lumper Loading' },
      { label: 'Lumper Unloading', value: 'Lumper Unloading' },
      { label: 'Out of Route Miles', value: 'Out of Route Miles' },
      { label: 'Positioning', value: 'Positioning' },
      { label: 'Protective Services', value: 'Protective Services' },
      { label: 'Re-Delivery', value: 'Re-Delivery' },
      { label: 'Route Survey', value: 'Route Survey' },
      { label: 'Storage', value: 'Storage' },
      { label: 'Team Service', value: 'Team Service' },
      { label: 'TONU', value: 'TONU' },
      { label: 'Wash', value: 'Wash' },
    ],
  },
  {
    label: 'Fees',
    items: [
      { label: 'Bonus', value: 'Bonus' },
      { label: 'Check Call', value: 'Check Call' },
      { label: 'Drop Trailer', value: 'Drop Trailer' },
      { label: 'Fee', value: 'Fee' },
      { label: 'Fine', value: 'Fine' },
      { label: 'GPS Tracking', value: 'GPS Tracking' },
      { label: 'Interstate/Highway Toll', value: 'Interstate/Highway Toll' },
      { label: 'Late Fee', value: 'Late Fee' },
      { label: 'New York Delivery Charge', value: 'New York Delivery Charge' },
      { label: 'On-time Delivery', value: 'On-time Delivery' },
      { label: 'On-time Pick Up', value: 'On-time Pick Up' },
      { label: 'Over Weight', value: 'Over Weight' },
      { label: 'Pallet Charge', value: 'Pallet Charge' },
      { label: 'Permits', value: 'Permits' },
      { label: 'Premium Charge', value: 'Premium Charge' },
      { label: 'Special Handling Service', value: 'Special Handling Service' },
      { label: 'Tarp Fee', value: 'Tarp Fee' },
      { label: 'Unloading', value: 'Unloading' },
    ],
  },
  {
    label: 'Intermodal',
    items: [
      { label: 'Block And Brace', value: 'Block And Brace' },
      { label: 'Chassis', value: 'Chassis' },
      { label: 'Diversion', value: 'Diversion' },
      { label: 'Driver Assist', value: 'Driver Assist' },
      { label: 'Driver Count', value: 'Driver Count' },
      { label: 'Driver Dunnage Disposal', value: 'Driver Dunnage Disposal' },
      { label: 'Driver Layover', value: 'Driver Layover' },
      { label: 'Driver Sweep', value: 'Driver Sweep' },
      { label: 'Driver Wash Out', value: 'Driver Wash Out' },
      {
        label: 'Dry Run (TONU) Equipment Not Used',
        value: 'Dry Run (TONU) Equipment Not Used',
      },
      { label: 'Hazmat', value: 'Hazmat' },
      { label: 'High Value Seal Charge', value: 'High Value Seal Charge' },
      { label: 'Lumper Loading', value: 'Lumper Loading' },
      { label: 'Lumper Unloading', value: 'Lumper Unloading' },
      { label: 'Miscellaneous', value: 'Miscellaneous' },
      { label: 'Night Delivery', value: 'Night Delivery' },
      { label: 'Pier Pass', value: 'Pier Pass' },
      {
        label: 'Power Detention Live Unload',
        value: 'Power Detention Live Unload',
      },
      {
        label: 'Power Detention Drop and Hook',
        value: 'Power Detention Drop and Hook',
      },
      { label: 'Pre-Pull', value: 'Pre-Pull' },
      { label: 'Reconsignment', value: 'Reconsignment' },
      { label: 'Scale Light and Heavy', value: 'Scale Light and Heavy' },
      { label: 'Single Scale Charge', value: 'Single Scale Charge' },
      { label: 'Stop Off', value: 'Stop Off' },
      { label: 'Tri-Axle', value: 'Tri-Axle' },
      { label: 'Yard Pull', value: 'Yard Pull' },
      { label: 'Yard Storage', value: 'Yard Storage' },
    ],
  },
  {
    label: 'LTL',
    items: [
      { label: 'Additional Freight', value: 'Additional Freight' },
      { label: 'Adjustment', value: 'Adjustment' },
      { label: 'Agent Authorized SED', value: 'Agent Authorized SED' },
      { label: 'Airport Delivery', value: 'Airport Delivery' },
      { label: 'Airport Pickup', value: 'Airport Pickup' },
      { label: 'Appointment Delivery', value: 'Appointment Delivery' },
      { label: 'Appointment Pickup', value: 'Appointment Pickup' },
      { label: 'Bill of Lading', value: 'Bill of Lading' },
      { label: 'Blind Shipment', value: 'Blind Shipment' },
      { label: 'CarrierCharge', value: 'CarrierCharge' },
      { label: 'CFS Delivery', value: 'CFS Delivery' },
      { label: 'CFS Pickup', value: 'CFS Pickup' },
      { label: 'Clean Air Surcharge', value: 'Clean Air Surcharge' },
      { label: 'COI Required', value: 'COI Required' },
      { label: 'Correction of Terms', value: 'Correction of Terms' },
      { label: 'Cubic Capacity', value: 'Cubic Capacity' },
      { label: 'Dimensional Weight', value: 'Dimensional Weight' },
      { label: 'Discount', value: 'Discount' },
      { label: 'Gate Fee', value: 'Gate Fee' },
      { label: 'Ground Delivery Service', value: 'Ground Delivery Service' },
      { label: 'Guaranteed Delivery Day', value: 'Guaranteed Delivery Day' },
      { label: 'GST/HST', value: 'GST/HST' },
      { label: 'Handling Fee', value: 'Handling Fee' },
      { label: 'High Cost Area Fee', value: 'High Cost Area Fee' },
      { label: 'Inspection Fee', value: 'Inspection Fee' },
      { label: 'Inside Delivery', value: 'Inside Delivery' },
      { label: 'Inside Pickup', value: 'Inside Pickup' },
      { label: 'Interstate/Highway Toll', value: 'Interstate/Highway Toll' },
      { label: 'Labeling', value: 'Labeling' },
      { label: 'Labor', value: 'Labor' },
      { label: 'Liftgate Delivery', value: 'Liftgate Delivery' },
      { label: 'Liftgate Pickup', value: 'Liftgate Pickup' },
      { label: 'Limited Access Delivery', value: 'Limited Access Delivery' },
      { label: 'Limited Access Pickup', value: 'Limited Access Pickup' },
      { label: 'LOA', value: 'LOA' },
    ],
  },
  {
    label: 'Drayage',
    items: [
      { label: 'Chassis Split', value: 'Chassis Split' },
      { label: 'Flat Rack Fee', value: 'Flat Rack Fee' },
      { label: 'Flip Fee', value: 'Flip Fee' },
      { label: 'Gen Set Fee', value: 'Gen Set Fee' },
      { label: 'Out of Gauge Fee', value: 'Out of Gauge Fee' },
      { label: 'Port Fee', value: 'Port Fee' },
      { label: 'Tanker Endorsement', value: 'Tanker Endorsement' },
      { label: 'Tolls', value: 'Tolls' },
      { label: 'Triaxle', value: 'Triaxle' },
    ],
  },
  {
    label: 'Misc',
    items: [
      { label: 'Misc', value: 'Misc' },
      { label: 'New York Delivery Charge', value: 'New York Delivery Charge' },
      { label: 'Notification Delivery', value: 'Notification Delivery' },
      { label: 'Paid Wrong Carrier', value: 'Paid Wrong Carrier' },
      { label: 'Premium Charge', value: 'Premium Charge' },
      { label: 'Protect From Freeze', value: 'Protect From Freeze' },
      { label: 'Re-Class', value: 'Re-Class' },
      { label: 'Redelivery', value: 'Redelivery' },
      { label: 'Residential Delivery', value: 'Residential Delivery' },
      { label: 'Residential Pickup', value: 'Residential Pickup' },
      { label: 'Reweigh Fee', value: 'Reweigh Fee' },
      { label: 'Saturday Pickup', value: 'Saturday Pickup' },
      { label: 'Seal', value: 'Seal' },
      { label: 'Short Pay', value: 'Short Pay' },
      { label: 'Sort & Segregate', value: 'Sort & Segregate' },
      {
        label: 'Sort & Segregate Delivery',
        value: 'Sort & Segregate Delivery',
      },
      { label: 'Sort & Segregate Pickup', value: 'Sort & Segregate Pickup' },
      { label: 'Special Handling Service', value: 'Special Handling Service' },
      { label: 'Tarp', value: 'Tarp' },
      { label: 'Trade Show Delivery', value: 'Trade Show Delivery' },
      { label: 'Trade Show Pickup', value: 'Trade Show Pickup' },
      { label: 'Warehouse Fee', value: 'Warehouse Fee' },
      { label: 'White Glove Service', value: 'White Glove Service' },
      { label: 'Write Off', value: 'Write Off' },
    ],
  },
];

export const lineItemsTypesForCarrierPay = [
  {
    label: 'Pricing',
    value: 'Pricing',
    disabled: true,
  },
  { label: 'Line Haul', value: 'Line Haul' },
  { label: 'Rate per Mile', value: 'Rate per Mile' },
  { label: 'Rate per Pound', value: 'Rate per Pound' },
  { label: 'Rate per Unit', value: 'Rate per Unit' },
  {
    label: 'Accessorials',
    value: 'Accessorials',
    disabled: true,
  },
  { label: 'Accessorial', value: 'Accessorial' },
  { label: 'Bucket Truck', value: 'Bucket Truck' },
  { label: 'Damage', value: 'Damage' },
  { label: 'Dead Head', value: 'Dead Head' },
  { label: 'Detention', value: 'Detention' },
  { label: 'Detention Loading', value: 'Detention Loading' },
  { label: 'Detention Unloading', value: 'Detention Unloading' },
  { label: 'Driver Assist', value: 'Driver Assist' },
  { label: 'Escort', value: 'Escort' },
  { label: 'Fuel', value: 'Fuel' },
  { label: 'Fumigation', value: 'Fumigation' },
  { label: 'Insurance', value: 'Insurance' },
  { label: 'Layover', value: 'Layover' },
  { label: 'Lumper', value: 'Lumper' },
  { label: 'Lumper Loading', value: 'Lumper Loading' },
  { label: 'Lumper Unloading', value: 'Lumper Unloading' },
  { label: 'Out of Route Miles', value: 'Out of Route Miles' },
  { label: 'Positioning', value: 'Positioning' },
  { label: 'Protective Services', value: 'Protective Services' },
  { label: 'Re-Delivery', value: 'Re-Delivery' },
  { label: 'Route Survey', value: 'Route Survey' },
  { label: 'Storage', value: 'Storage' },
  { label: 'Team Service', value: 'Team Service' },
  { label: 'TONU', value: 'TONU' },
  { label: 'Wash', value: 'Wash' },
  {
    label: 'Fees',
    value: 'Fees',
    disabled: true,
  },
  { label: 'Bonus', value: 'Bonus' },
  { label: 'Check Call', value: 'Check Call' },
  { label: 'Drop Trailer', value: 'Drop Trailer' },
  { label: 'Fee', value: 'Fee' },
  { label: 'Fine', value: 'Fine' },
  { label: 'GPS Tracking', value: 'GPS Tracking' },
  { label: 'Interstate/Highway Toll', value: 'Interstate/Highway Toll' },
  { label: 'Late Fee', value: 'Late Fee' },
  { label: 'New York Delivery Charge', value: 'New York Delivery Charge' },
  { label: 'On-time Delivery', value: 'On-time Delivery' },
  { label: 'On-time Pick Up', value: 'On-time Pick Up' },
  { label: 'Over Weight', value: 'Over Weight' },
  { label: 'Pallet Charge', value: 'Pallet Charge' },
  { label: 'Permits', value: 'Permits' },
  { label: 'Premium Charge', value: 'Premium Charge' },
  { label: 'Special Handling Service', value: 'Special Handling Service' },
  { label: 'Tarp Fee', value: 'Tarp Fee' },
  { label: 'Unloading', value: 'Unloading' },
  {
    label: 'Intermodal',
    value: 'Intermodal',
    disabled: true,
  },
  { label: 'Block And Brace', value: 'Block And Brace' },
  { label: 'Chassis', value: 'Chassis' },
  { label: 'Diversion', value: 'Diversion' },
  { label: 'Driver Assist', value: 'Driver Assist' },
  { label: 'Driver Count', value: 'Driver Count' },
  { label: 'Driver Dunnage Disposal', value: 'Driver Dunnage Disposal' },
  { label: 'Driver Layover', value: 'Driver Layover' },
  { label: 'Driver Sweep', value: 'Driver Sweep' },
  { label: 'Driver Wash Out', value: 'Driver Wash Out' },
  {
    label: 'Dry Run (TONU) Equipment Not Used',
    value: 'Dry Run (TONU) Equipment Not Used',
  },
  { label: 'Hazmat', value: 'Hazmat' },
  { label: 'High Value Seal Charge', value: 'High Value Seal Charge' },
  { label: 'Lumper Loading', value: 'Lumper Loading' },
  { label: 'Lumper Unloading', value: 'Lumper Unloading' },
  { label: 'Miscellaneous', value: 'Miscellaneous' },
  { label: 'Night Delivery', value: 'Night Delivery' },
  { label: 'Pier Pass', value: 'Pier Pass' },
  {
    label: 'Power Detention Live Unload',
    value: 'Power Detention Live Unload',
  },
  {
    label: 'Power Detention Drop and Hook',
    value: 'Power Detention Drop and Hook',
  },
  { label: 'Pre-Pull', value: 'Pre-Pull' },
  { label: 'Reconsignment', value: 'Reconsignment' },
  { label: 'Scale Light and Heavy', value: 'Scale Light and Heavy' },
  { label: 'Single Scale Charge', value: 'Single Scale Charge' },
  { label: 'Stop Off', value: 'Stop Off' },
  { label: 'Tri-Axle', value: 'Tri-Axle' },
  { label: 'Yard Pull', value: 'Yard Pull' },
  { label: 'Yard Storage', value: 'Yard Storage' },
  {
    label: 'LTL',
    value: 'LTL',
    disabled: true,
  },
  { label: 'Additional Freight', value: 'Additional Freight' },
  { label: 'Adjustment', value: 'Adjustment' },
  { label: 'Agent Authorized SED', value: 'Agent Authorized SED' },
  { label: 'Airport Delivery', value: 'Airport Delivery' },
  { label: 'Airport Pickup', value: 'Airport Pickup' },
  { label: 'Appointment Delivery', value: 'Appointment Delivery' },
  { label: 'Appointment Pickup', value: 'Appointment Pickup' },
  { label: 'Bill of Lading', value: 'Bill of Lading' },
  { label: 'Blind Shipment', value: 'Blind Shipment' },
  { label: 'CarrierCharge', value: 'CarrierCharge' },
  { label: 'CFS Delivery', value: 'CFS Delivery' },
  { label: 'CFS Pickup', value: 'CFS Pickup' },
  { label: 'Clean Air Surcharge', value: 'Clean Air Surcharge' },
  { label: 'COI Required', value: 'COI Required' },
  { label: 'Correction of Terms', value: 'Correction of Terms' },
  { label: 'Cubic Capacity', value: 'Cubic Capacity' },
  { label: 'Dimensional Weight', value: 'Dimensional Weight' },
  { label: 'Discount', value: 'Discount' },
  { label: 'Gate Fee', value: 'Gate Fee' },
  { label: 'Ground Delivery Service', value: 'Ground Delivery Service' },
  { label: 'Guaranteed Delivery Day', value: 'Guaranteed Delivery Day' },
  { label: 'GST/HST', value: 'GST/HST' },
  { label: 'Handling Fee', value: 'Handling Fee' },
  { label: 'High Cost Area Fee', value: 'High Cost Area Fee' },
  { label: 'Inspection Fee', value: 'Inspection Fee' },
  { label: 'Inside Delivery', value: 'Inside Delivery' },
  { label: 'Inside Pickup', value: 'Inside Pickup' },
  { label: 'Interstate/Highway Toll', value: 'Interstate/Highway Toll' },
  { label: 'Labeling', value: 'Labeling' },
  { label: 'Labor', value: 'Labor' },
  { label: 'Liftgate Delivery', value: 'Liftgate Delivery' },
  { label: 'Liftgate Pickup', value: 'Liftgate Pickup' },
  { label: 'Limited Access Delivery', value: 'Limited Access Delivery' },
  { label: 'Limited Access Pickup', value: 'Limited Access Pickup' },
  { label: 'LOA', value: 'LOA' },
  { label: 'Drayage', value: 'Drayage', disabled: true },
  { label: 'Chassis Split', value: 'Chassis Split' },
  { label: 'Flat Rack Fee', value: 'Flat Rack Fee' },
  { label: 'Flip Fee', value: 'Flip Fee' },
  { label: 'Gen Set Fee', value: 'Gen Set Fee' },
  { label: 'Out of Gauge Fee', value: 'Out of Gauge Fee' },
  { label: 'Port Fee', value: 'Port Fee' },
  { label: 'Tanker Endorsement', value: 'Tanker Endorsement' },
  { label: 'Tolls', value: 'Tolls' },
  { label: 'Triaxle', value: 'Triaxle' },
  { label: 'Misc', value: 'Misc', disabled: true },
  { label: 'Misc', value: 'Misc' },
  { label: 'New York Delivery Charge', value: 'New York Delivery Charge' },
  { label: 'Notification Delivery', value: 'Notification Delivery' },
  { label: 'Paid Wrong Carrier', value: 'Paid Wrong Carrier' },
  { label: 'Premium Charge', value: 'Premium Charge' },
  { label: 'Protect From Freeze', value: 'Protect From Freeze' },
  { label: 'Re-Class', value: 'Re-Class' },
  { label: 'Redelivery', value: 'Redelivery' },
  { label: 'Residential Delivery', value: 'Residential Delivery' },
  { label: 'Residential Pickup', value: 'Residential Pickup' },
  { label: 'Reweigh Fee', value: 'Reweigh Fee' },
  { label: 'Saturday Pickup', value: 'Saturday Pickup' },
  { label: 'Seal', value: 'Seal' },
  { label: 'Short Pay', value: 'Short Pay' },
  { label: 'Sort & Segregate', value: 'Sort & Segregate' },
  { label: 'Sort & Segregate Delivery', value: 'Sort & Segregate Delivery' },
  { label: 'Sort & Segregate Pickup', value: 'Sort & Segregate Pickup' },
  { label: 'Special Handling Service', value: 'Special Handling Service' },
  { label: 'Tarp', value: 'Tarp' },
  { label: 'Trade Show Delivery', value: 'Trade Show Delivery' },
  { label: 'Trade Show Pickup', value: 'Trade Show Pickup' },
  { label: 'Warehouse Fee', value: 'Warehouse Fee' },
  { label: 'White Glove Service', value: 'White Glove Service' },
  { label: 'Write Off', value: 'Write Off' },
];

export const adjustmentsOptions = [
  { label: '90 Days Retraction', value: 1 },
  { label: '120 Days Retraction', value: 2 },
  { label: '90 Days Refund', value: 3 },
  { label: '120 Days Refund', value: 5 },
  { label: 'In Collections', value: 4 },
];

export const vendorServiceTypes = [
  'Airline/Air Cargo',
  'Bucket Truck',
  'Cross Docking',
  'Customs Brokerage Service',
  'Defrauded Carrier',
  'Drayage',
  'Escort',
  'Excess Cargo Insurance',
  'Forklift Service',
  'Hauling Carrier',
  'Inspection',
  'International',
  'Labor',
  'LTL Insurance',
  'Lumper Services',
  'Ocean Freight- AFL',
  'Ocean Freight- CAN',
  'Other',
  'Railroad',
  'Rebate',
  'Reimbursement',
  'Rental Services',
  'Route Survey',
  'Security',
  'Standard Cargo Insurance',
  'Survey',
  'Tarping Service',
  'Terminal Handling',
  'Tow/Wrecker Service',
  'Transloading Service',
  'Warehousing',
];

//need to tech debt this
export const vendorStatusTypes = ['Open', 'Billed', 'Paid'];

// DialogBox IDs

export const DELETE_DOCUMENT_PAGE_DIALOG_ID = 'deleteDocumentPageDialog';
export const DELETE_CARRIER_PLAYLIST_DIALOG_ID = 'deleteCarrierPlaylistDialog';

export const reeferValidationErrorName = 'ReeferValidationError';
export const customerStatusValidationErrorName =
  'CustomerStatusValidationError';
